/**
 * Dataset 상세의 섹션 축 — 창과 route 페이지가 같은 섹션 목록 · 잠김 사유 · 이웃 규칙을 쓴다.
 *
 * route 페이지에서는 URL(`?section=`)이 값을 소유하고(기본값은 URL 에서 뺀다), 창에서는 창 로컬
 * 상태가 소유한다. 어느 쪽이든 항목의 모양은 여기 한 곳이 만든다.
 *
 * Owns:
 * - 섹션 키 · 라벨 · URL 코덱 · 잠김 사유 · 이웃(←/→) 규칙.
 *
 * Does not own:
 * - 그리기 — `page/components/DatasetSectionTabs.tsx`. 헤더의 상태 chip — `datasetDetailBadge.tsx`.
 */
import type { Dataset } from "../domain/dataset";
import { formatProbeLockReason } from "./datasetFormatters";

export type DatasetSection = "overview" | "files" | "probes" | "inspect" | "facts";

export const DATASET_SECTIONS: readonly DatasetSection[] = [
  "overview",
  "files",
  "probes",
  "inspect",
  "facts",
];

export const DEFAULT_DATASET_SECTION: DatasetSection = "overview";

export const DATASET_SECTION_LABEL: Record<DatasetSection, string> = {
  overview: "개요",
  files: "파일",
  probes: "Probe",
  inspect: "점검·매핑",
  facts: "조사 사실",
};

const SECTION_PARAM = "section";

export function isDatasetSection(value: unknown): value is DatasetSection {
  return typeof value === "string" && (DATASET_SECTIONS as readonly string[]).includes(value);
}

export function readDatasetSection(params: URLSearchParams): DatasetSection {
  const value = params.get(SECTION_PARAM);
  return isDatasetSection(value) ? value : DEFAULT_DATASET_SECTION;
}

/** 기본값인 섹션은 URL 에서 지운다 — 처음 여는 화면의 주소가 길어지지 않게. */
export function writeDatasetSection(
  params: URLSearchParams,
  section: DatasetSection,
): URLSearchParams {
  const next = new URLSearchParams(params);
  if (section === DEFAULT_DATASET_SECTION) {
    next.delete(SECTION_PARAM);
  } else {
    next.set(SECTION_PARAM, section);
  }
  return next;
}

export type DatasetSectionDescriptor = {
  key: DatasetSection;
  label: string;
  /** 고를 수 없는 항목의 사유. 탭이 툴팁으로 띄운다. `null` 이면 고를 수 있다. */
  disabledReason: string | null;
};

/**
 * 다섯 섹션의 탭 항목.
 *
 * **파일은 언제나 열린다.** 사용자는 엔진이 읽지 못하는 원본의 내용도 봐야 한다. 그런 원본에서는
 * 파일이 화면의 전부다. Probe · 점검은 서버가 `canProbe=false` 로 판정하면 사유와 함께 잠근다. 서버도
 * 같은 판정으로 Probe 요청을 거부한다. 그래서 탭만 열어 두면 요청은 반드시 실패한다. **조사 사실도
 * 언제나 열린다** — 원본을 읽지 못해도 그 원본에 대해 조사한 사실은 적을 수 있다.
 */
export function describeDatasetSections(
  dataset: Dataset | null,
): readonly DatasetSectionDescriptor[] {
  const locked = lockReason(dataset);
  return DATASET_SECTIONS.map((key) => ({
    key,
    label: DATASET_SECTION_LABEL[key],
    disabledReason: key === "probes" || key === "inspect" ? locked : null,
  }));
}

/** 서버가 Probe 불가로 판정한 원본의 잠김 사유. Dataset 을 아직 받지 못했으면 잠그지 않는다. */
function lockReason(dataset: Dataset | null): string | null {
  if (dataset === null || dataset.canProbe) {
    return null;
  }
  return formatProbeLockReason(dataset.formatDetail);
}

/**
 * 다음/이전 섹션 — 고를 수 있는 것 안에서만 움직이고 끝에서는 멈춘다(순환하면 마지막에서 한 번
 * 더 눌렀을 때 처음으로 튀어 어디까지 봤는지 감각이 끊긴다). 갈 곳이 없으면 `null`.
 */
export function adjacentSectionKey(
  entries: readonly DatasetSectionDescriptor[],
  active: DatasetSection,
  step: 1 | -1,
): DatasetSection | null {
  const selectable = entries
    .filter((entry) => entry.disabledReason === null)
    .map((entry) => entry.key);
  return selectable[selectable.indexOf(active) + step] ?? null;
}
